import { Link, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { X } from 'lucide-react'
import type { DocsCategory } from '@/routes/documentation/_data'

interface DocsSidebarProps {
  categories: DocsCategory[]
  mobileOpen: boolean
  onMobileClose: () => void
}

function SidebarContent({ categories }: { categories: DocsCategory[] }) {
  const location = useLocation()
  const currentSlug = location.pathname.split('/documentation/')[1] || 'getting-started'

  return (
    <nav className="p-4 space-y-6">
      {categories.map((category) => (
        <div key={category.label}>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
            {category.label}
          </h3>
          <ul className="space-y-0.5">
            {category.articles.map((article) => {
              const isActive = currentSlug === article.slug
              const Icon = article.icon
              return (
                <li key={article.slug}>
                  <Link
                    to={`/documentation/${article.slug}`}
                    className={cn(
                      'flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors',
                      isActive
                        ? 'bg-muted text-foreground font-medium'
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                    )}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    {article.title}
                  </Link>
                </li>
              )
            })}
          </ul>
        </div>
      ))}
    </nav>
  )
}

export function DocsSidebar({ categories, mobileOpen, onMobileClose }: DocsSidebarProps) {
  return (
    <>
      {/* Desktop sidebar */}
      <aside className="hidden md:block w-64 shrink-0 sticky top-16 h-[calc(100vh-4rem)] overflow-y-auto border-r border-border">
        <SidebarContent categories={categories} />
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={onMobileClose}
          />
          <div className="absolute left-0 top-0 bottom-0 w-64 bg-background shadow-xl overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <span className="text-sm font-semibold">Documentation</span>
              <button
                onClick={onMobileClose}
                className="p-1 rounded-md hover:bg-muted"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <SidebarContent categories={categories} />
          </div>
        </div>
      )}
    </>
  )
}
